import axios, { AxiosResponse } from "axios";
import { useMutation } from "react-query";

const apiBaseUrl =
  process.env.REACT_APP_API_BASEURL + "/BusinessLicensePermits";

type FormDataValue = string | number | boolean | null | undefined;

interface NumericSelectValue {
  value?: string | number | null;
}

export interface AddBLPRequest {
  officeId?: number | null;
  issuer?: number | null;
  issuerPhone?: string | null;
  type?: number | null;
  licenseOrPermitNumber?: string | null;
  issuedDate?: string | null;
  renewedDate?: string | null;
  licenseStatus?: number | null;
  renewalStatus?: number | null;
  isLicenseRequired?: boolean | null;
  isOldLicense?: boolean | null;
  expirationDate?: string | null;
  isActive?: boolean | null;
  notes?: string | null;
  attachments?: (File | null)[];
}

const appendIfDefined = (
  formData: FormData,
  key: string,
  value: FormDataValue,
) => {
  if (value === null || value === undefined || value === "") {
    return;
  }

  formData.append(key, String(value));
};

const getNumericSourceValue = (value: unknown): unknown => {
  if (typeof value === "object" && value !== null && "value" in value) {
    return (value as NumericSelectValue).value;
  }

  return value;
};

const appendNumberIfDefined = (
  formData: FormData,
  key: string,
  value: unknown,
) => {
  const sourceValue = getNumericSourceValue(value);

  if (sourceValue === null || sourceValue === undefined || sourceValue === "") {
    return;
  }

  const numericValue = Number(sourceValue);

  if (!Number.isFinite(numericValue)) {
    throw new TypeError(`${key} must contain a valid numeric ID.`);
  }

  if (numericValue <= 0) {
    return;
  }

  formData.append(key, String(numericValue));
};

const getPositiveNumberOrNull = (value: unknown): number | null => {
  const sourceValue = getNumericSourceValue(value);

  if (sourceValue === null || sourceValue === undefined || sourceValue === "") {
    return null;
  }

  const numericValue = Number(sourceValue);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  return numericValue;
};

const createAddBLPFormData = (newBLP: AddBLPRequest): FormData => {
  const formData = new FormData();

  newBLP.attachments?.forEach((file) => {
    if (file) {
      formData.append("attachments", file);
    }
  });

  appendNumberIfDefined(formData, "officeId", newBLP.officeId);

  appendNumberIfDefined(formData, "issuer", newBLP.issuer);

  appendIfDefined(formData, "issuerPhone", newBLP.issuerPhone);

  const typeId = getPositiveNumberOrNull(newBLP.type);

  appendNumberIfDefined(formData, "type", typeId);

  appendIfDefined(
    formData,
    "licenseOrPermitNumber",
    newBLP.licenseOrPermitNumber,
  );

  appendIfDefined(formData, "issuedDate", newBLP.issuedDate);

  appendIfDefined(formData, "renewedDate", newBLP.renewedDate);

  appendNumberIfDefined(formData, "licenseStatusId", newBLP.licenseStatus);

  appendNumberIfDefined(formData, "renewalStatusId", newBLP.renewalStatus);

  if (typeof newBLP.isOldLicense === "boolean") {
    formData.append("isOldLicense", newBLP.isOldLicense ? "true" : "false");
  }

  appendIfDefined(formData, "expirationDate", newBLP.expirationDate);

  appendIfDefined(formData, "notes", newBLP.notes);

  return formData;
};

export const addBLP = async (
  newBLP: AddBLPRequest,
): Promise<AxiosResponse<void>> => {
  const formData = createAddBLPFormData(newBLP);

  return axios.post<void>(apiBaseUrl, formData);
};

export const useAddBLP = () => {
  return useMutation<AxiosResponse<void>, unknown, AddBLPRequest>(addBLP);
};
